import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { Flex } from "@similarweb/ui-components/foundations";

import ColorStack from "app/components/colorsStack/ColorStack";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { XAxisText, YAxisText } from "app/pages/industry-analysis/players/table/bubble-chart/chart/AxisText";
import { getChartConfig } from "app/pages/industry-analysis/players/table/bubble-chart/chart/getChartConfig";
import { QuadrantTitles } from "app/pages/industry-analysis/players/table/bubble-chart/chart/QuadrantTitles";
import {
    CHART_HEIGHT,
    CHART_OPTIONS_META_DATA,
    INITIAL_SELECTION,
    INITIAL_SELECTION_PERIOD_OVER_PERIOD,
} from "app/pages/industry-analysis/players/table/bubble-chart/const";
import {
    getBubbleChartInitialSelectionIds,
    parseRawData,
} from "app/pages/industry-analysis/players/table/bubble-chart/parseRawData";
import type { SelectedEntitiesIDsT } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import { ETypes } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import {
    getRawDataWithoutSegments,
    getTrackingFilters,
} from "app/pages/industry-analysis/players/table/bubble-chart/utils";
import type { IRawData } from "app/pages/industry-analysis/players/table/types";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import DurationService from "app/services/DurationService";
import { isLinesOfBusinessSegment } from "app/services/segments/LinesOfBusinessUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";

import Chart from "pro-features/components/Chart/src/Chart";
import { NoData as BubbleChartEmptySelection } from "pro-features/components/NoData/src/NoData";
import { Legends } from "./Legends";

const LEGENDS_CONTAINER_WIDTH = "245px";
const BULLET_SIGN = "•";

const ChartContainer = styled.div`
    /** cant use flex due to highcharts bug */
    width: calc(100% - ${LEGENDS_CONTAINER_WIDTH});
    height: ${CHART_HEIGHT.length}${CHART_HEIGHT.unit};
`;

const Container = styled.div`
    display: flex;
    padding: 0 0px 24px 0px;
`;

const ChartAndLegendsContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 8px;
`;

interface IBubbleChart {
    rawData: IRawData;
    type: ETypes;
    showAverage: boolean;
}

export const BubbleChart: FC<IBubbleChart> = (props) => {
    const { rawData: rawDataProp, type, showAverage } = props;

    const rawData = rawDataProp;
    const isSegmentSupported = CHART_OPTIONS_META_DATA[type].isSegmentsEnabled;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const colorStack = useRef(new ColorStack(CHART_COLORS.chartMainColorsWithoutOthers));

    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration, category } = params;
    const isCustomIndustry = UserCustomCategoryService.isCustomCategory(category);
    const filters = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration).forAPI,
        [duration, comparedDuration],
    );

    const [selectedEntitiesIDs, setSelectedEntitiesIDs] = useState<SelectedEntitiesIDsT>(() => {
        const limit = isPeriodOverPeriod ? INITIAL_SELECTION_PERIOD_OVER_PERIOD : INITIAL_SELECTION;
        return new Set(getBubbleChartInitialSelectionIds(rawDataProp, type, isPeriodOverPeriod, limit));
    });

    useEffect(() => {
        colorStack.current = new ColorStack(CHART_COLORS.chartMainColorsWithoutOthers);
        const limit = isPeriodOverPeriod ? INITIAL_SELECTION_PERIOD_OVER_PERIOD : INITIAL_SELECTION;
        const nextIds = getBubbleChartInitialSelectionIds(rawDataProp, type, isPeriodOverPeriod, limit);
        setSelectedEntitiesIDs(new Set(nextIds));
    }, [category, rawDataProp, type, isPeriodOverPeriod]);
    const selectedEntitiesColorMap = useMemo(() => {
        const newSelectedEntitiesColorMap = new Map<string, string>();

        rawData.Data.forEach(({ Id }) => {
            if (!newSelectedEntitiesColorMap.has(Id)) {
                newSelectedEntitiesColorMap.set(Id, colorStack.current.acquire());
            }
        });

        return newSelectedEntitiesColorMap;
    }, [rawData.Data]);

    const segmentsModule: ISegmentsModule = useSelector((state) => state?.segmentsModule);
    const getUserAndAccountSegmentById = useMemo(
        () => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule),
        [segmentsModule],
    );

    const segmentNameGetter = (id: string): string | undefined => {
        const segmentName = getUserAndAccountSegmentById(id)?.segmentName;
        if (segmentName) {
            const prefixText = "market.research.players.Quadrant.bubble.tooltip.name.segment";
            return `${translate(prefixText)} ${BULLET_SIGN} ${segmentName}`;
        }

        if (isLinesOfBusinessSegment(id)) {
            const lobItem = rawData.Data.find((item) => item.Id === id);
            if (lobItem?.DisplayName) {
                return lobItem.DisplayName;
            }
        }
    };

    const chartData = useMemo(() => {
        const newRawData = isSegmentSupported ? rawData : getRawDataWithoutSegments(rawData);
        const segmentNameGetterFunction = isSegmentSupported ? segmentNameGetter : undefined;
        return parseRawData(
            newRawData,
            type,
            selectedEntitiesIDs,
            selectedEntitiesColorMap,
            segmentNameGetterFunction,
            isPeriodOverPeriod,
        );
    }, [rawData, type, selectedEntitiesIDs, selectedEntitiesColorMap, isPeriodOverPeriod]);

    const chartConfig = useMemo(() => {
        if (!chartData || chartData?.length === 0) {
            return {};
        }
        return getChartConfig(chartData, type, showAverage, isPeriodOverPeriod, filters);
    }, [chartData, type, showAverage, isPeriodOverPeriod, filters]);

    const selectedEntitiesIDsHash = Array.from(selectedEntitiesIDs).join();

    const [isChartRendered, setIsChartRendered] = useState(false);

    useEffect(() => {
        if (chartData && chartData.length) {
            setIsChartRendered(true);
        } else {
            setIsChartRendered(false);
        }
    }, [chartData]);

    const trackOnLegendClick = (
        domain: string,
        displayName: string,
        isChecked: boolean,
        position: number,
        averageMonthlyVisits: number,
    ) => {
        const defaultFilters = getTrackingFilters(type, showAverage);
        const trackingFilters = {
            ...defaultFilters,
            entityName: displayName,
            domain,
            averageMonthlyVisits,
            position,
        };
        const action = isChecked ? "uncheck" : "check";
        allTrackers.dispatchTrackEvent("Click Filter", action, "QuadrantType", trackingFilters);
    };

    const handleToggleEntity = (id: string) => {
        const newSelectedEntitiesIDs = new Set(selectedEntitiesIDs);
        if (newSelectedEntitiesIDs.has(id)) {
            newSelectedEntitiesIDs.delete(id);
        } else {
            newSelectedEntitiesIDs.add(id);
        }
        setSelectedEntitiesIDs(newSelectedEntitiesIDs);
    };

    if (!chartData) {
        return null;
    }
    const isDatasetEmpty = selectedEntitiesIDs.size === 0;

    return (
        <Container>
            {isDatasetEmpty ? null : <YAxisText type={type} />}
            <ChartAndLegendsContainer>
                <ChartContainer>
                    {isDatasetEmpty ? (
                        <Flex
                            width="100%"
                            height="100%"
                            alignItems="center"
                            justifyContent="center"
                            data-automation="market-players-bubble-chart-no-selection"
                        >
                            <BubbleChartEmptySelection
                                iconName="no-data-lab"
                                title="market.research.players.bubble.no.selection.title"
                                subtitle="market.research.players.bubble.no.selection.subtitle"
                            />
                        </Flex>
                    ) : (
                        <Chart
                            type="bubble"
                            data={chartData}
                            config={chartConfig}
                            key={selectedEntitiesIDsHash}
                            onRender={() => setIsChartRendered(true)}
                        />
                    )}
                    {isChartRendered ? <QuadrantTitles type={type} /> : null}
                    {isDatasetEmpty ? null : (
                        <XAxisText
                            type={type}
                            isCustomIndustry={isCustomIndustry}
                        />
                    )}
                </ChartContainer>
                <Legends
                    rawData={rawData}
                    chartType={type}
                    selectedEntitiesIDs={selectedEntitiesIDs}
                    onToggleEntity={handleToggleEntity}
                    trackOnLegendClick={trackOnLegendClick}
                    selectedEntitiesColorMap={selectedEntitiesColorMap}
                    width={LEGENDS_CONTAINER_WIDTH}
                    isSegmentSupported={isSegmentSupported}
                    showAvgUniqueUsers={type === ETypes.Loyalty}
                    isPeriodOverPeriod={isPeriodOverPeriod}
                />
            </ChartAndLegendsContainer>
        </Container>
    );
};
